import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { Button, TextField, Typography, Paper, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import ManagersModel, { ManagerDetailsData, managerSchema } from "../../core/api/manager/ManagersModel";
import { useNotifications } from "../../hooks/NotificationProvider";
import { translations } from "../../translations/common";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const ManagersForm: React.FC = () => {
  const [manager, setManager] = useState<ManagerDetailsData>({
    email: "",
    firstName: "",
    lastName: "",
    username: ""
  });

  const notifications = useNotifications();
  
  useEffect(() => {
    ManagersModel.getCurrentManager().then((m) => setManager({
      email: m.email,
      firstName: m.firstName,
      lastName: m.lastName,
      username: m.username
    }));
  }, []);

  return (
    <StyledPaper>
      <Formik
        enableReinitialize={true}
        initialValues={manager}
        onSubmit={(values, actions) => {
          ManagersModel.updateDetails(values)
            .catch((error) => {
              const data = error.response?.data;
              if (data?.errors) {
                data.errors.forEach((element: any) => {
                  actions.setFieldError(element.field, element.defaultMessage);
                });
              }
            })
            .then(() => {
              notifications.notify({
                title: translations.managers.notifications.saveSuccess.title,
                message: translations.managers.notifications.saveSuccess.message,
                level: "success",
              });
            })
            .finally(() => {
              actions.setSubmitting(false);
            });
        }}
        validationSchema={managerSchema.pick(['email', 'firstName', 'lastName'])}
      >
        {(props: FormikProps<ManagerDetailsData>) => {
          const {
            values,
            touched,
            errors,
            handleBlur,
            handleChange,
            isSubmitting,
          } = props;
          return (
            <Form>
              <Grid container direction="column" spacing={3} >
                <Grid size="grow">
                  <Typography variant="h5" gutterBottom>
                    {translations.managers.form.personalInfo}
                  </Typography>
                </Grid>
                <Grid size="grow">
                  <StyledTextField
                    error={errors.firstName && touched.firstName ? true : false}
                    id="firstName"
                    type="text"
                    name="firstName"
                    label={translations.managers.form.fields.firstName}
                    fullWidth
                    variant="outlined"
                    onChange={handleChange}
                    helperText={
                      touched.firstName && errors.firstName
                        ? errors.firstName
                        : ""
                    }
                    value={values.firstName}
                    onBlur={handleBlur}
                  />
                  <StyledTextField
                    error={errors.lastName && touched.lastName ? true : false}
                    id="lastName"
                    type="text"
                    name="lastName"
                    label={translations.managers.form.fields.lastName}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                    helperText={
                      touched.lastName && errors.lastName ? errors.lastName : ""
                    }
                    value={values.lastName}
                    onBlur={handleBlur}
                  />
                  <StyledTextField
                    error={errors.email && touched.email ? true : false}
                    id="email"
                    type="text"
                    name="email"
                    label={translations.managers.form.fields.email}
                    variant="outlined"
                    fullWidth
                    onChange={handleChange}
                    helperText={touched.email && errors.email ? errors.email : ""}
                    value={values.email}
                    onBlur={handleBlur}
                  />
                </Grid>
                <Grid size="grow">
                  <StyledButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                  >
                    {translations.managers.form.buttons.save}
                  </StyledButton>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </StyledPaper>
  );
};

export default ManagersForm;
